// Settings → Stations & devices (plan §10.1, §10.2): each branch's stations (the view they
// open on, their label printer), the devices serving them, pairing a new device with a
// one-time key, revoking one, and the minimum station app version. One column of cards,
// header and states included (review UI3A-8). A station just added is highlighted and its
// toast offers to pair a device to it (UI3A-208); a device below the minimum version says
// it has stopped, on its badge and on its station (UI3A-206).

import type { DeviceSummary, Station, StationsResponse } from '@enp/domain/api';
import {
  Button,
  Card,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from '@enp/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Ban,
  CircleAlert,
  CirclePause,
  KeyRound,
  Pencil,
  Plus,
  Printer,
  TabletSmartphone,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AccountDate } from '../../../shell/account-date.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import {
  MinVersionsDialog,
  PairDeviceDialog,
  RevokeDeviceDialog,
  StationDialog,
} from '../../../stations/components.js';
import {
  PLATFORM_LABEL,
  PLATFORM_ORDER,
  PROFILE_LABEL,
  deviceStatus,
  printerText,
} from '../../../stations/labels.js';
import {
  NoDeviceAccess,
  SettingsBreadcrumb,
  SettingsFrame,
  useAllBranches,
  useCanManageDevices,
} from '../../../stations/page-parts.js';
import { stationsQuery } from '../../../stations/queries.js';

export const Route = createFileRoute('/_app/settings/stations')({
  component: StationsPage,
});

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function DeviceRow({
  device,
  station,
  minVersion,
  onRevoked,
}: {
  device: DeviceSummary;
  station: Station;
  minVersion: string;
  onRevoked: (lastDevice: boolean) => void;
}) {
  const [revoking, setRevoking] = useState(false);
  const status = deviceStatus(device, minVersion);
  return (
    <TableRow>
      <TableCell>
        <span className="flex flex-col leading-tight">
          <span className="font-mono font-semibold text-fg">{device.device_code}</span>
          <span className="text-xs text-muted">{PLATFORM_LABEL[device.platform]}</span>
        </span>
      </TableCell>
      <TableCell className="tabular-nums">{device.app_version ?? '—'}</TableCell>
      <TableCell>
        {device.last_seen_at ? (
          <AccountDate value={device.last_seen_at} withTime />
        ) : (
          <span className="text-muted">Not yet</span>
        )}
      </TableCell>
      <TableCell>
        <StatusBadge status={status.status}>{status.label}</StatusBadge>
      </TableCell>
      <TableCell className="text-right">
        <Button
          size="sm"
          variant="destructive"
          icon={<Ban aria-hidden />}
          aria-label={`Revoke device ${device.device_code}`}
          onClick={() => setRevoking(true)}
        >
          Revoke
        </Button>
        <RevokeDeviceDialog
          device={device}
          station={station}
          open={revoking}
          onOpenChange={setRevoking}
          onRevoked={onRevoked}
        />
      </TableCell>
    </TableRow>
  );
}

/** "M2X has stopped syncing until its station app is updated to 0.1.0." (UI3A-206) */
function stoppedSentence(stopped: DeviceSummary[], data: StationsResponse): string {
  const codes = stopped.map((d) => d.device_code).join(', ');
  const versions = [...new Set(stopped.map((d) => data.min_app_versions[d.platform]))];
  return stopped.length === 1
    ? `${codes} has stopped syncing until its station app is updated to ${versions.join(' / ')}.`
    : `${codes} have stopped syncing until their station app is updated to ${versions.join(' / ')}.`;
}

function StationCard({
  station,
  data,
  headingLevel,
  highlighted,
  onPair,
}: {
  station: Station;
  data: StationsResponse;
  headingLevel: 'h2' | 'h3';
  /** Just added: outlined and brought into view (UI3A-208). */
  highlighted: boolean;
  /** Opens the pairing dialog for this station. */
  onPair: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const cardRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (highlighted) cardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [highlighted]);
  const Heading = headingLevel;
  const active = station.devices.filter((d) => !d.revoked_at);
  const revoked = station.devices.filter((d) => d.revoked_at);
  const stopped = active.filter((d) => d.below_min_version);
  const printer = printerText(station.printer);
  const headingId = `station-${station.id}`;
  /** After a revoke: the last device gone means the station needs a new one (UI3A-14). */
  const revokedOne = (device: DeviceSummary) => (lastDevice: boolean) => {
    toast.success(`Device ${device.device_code} revoked`, {
      description: lastDevice
        ? `It can’t sync any more, and ${station.name} has no device now.`
        : 'It can’t sync any more.',
      ...(lastDevice ? { action: { label: 'Pair a new device', onClick: onPair } } : {}),
    });
  };
  return (
    <li ref={cardRef} className="scroll-mt-24">
      <Card
        className={cn(
          'gap-4 p-5 transition-shadow',
          highlighted && 'border-primary/60 ring-2 ring-primary/25',
        )}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-col gap-1">
            <Heading id={headingId} className="font-display text-lg font-semibold text-fg">
              {station.name}
            </Heading>
            <p className="text-sm text-fg">
              Opens on <span className="font-medium">{PROFILE_LABEL[station.profile]}</span>
            </p>
            <p className="flex items-center gap-1.5 text-sm text-muted">
              <Printer aria-hidden className="size-4" />
              {printer ?? 'No label printer'}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              variant="secondary"
              icon={<Pencil aria-hidden />}
              aria-label={`Edit ${station.name}`}
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
            <Button
              variant={active.length === 0 ? 'primary' : 'secondary'}
              icon={<KeyRound aria-hidden />}
              aria-label={`Pair a device to ${station.name}`}
              onClick={onPair}
            >
              Pair a device
            </Button>
          </div>
        </div>

        {station.open_holds > 0 && (
          <p className="flex items-center gap-2 rounded-md bg-state-warning-subtle px-3 py-2 text-sm text-fg">
            <CircleAlert aria-hidden className="size-4 shrink-0 text-state-warning" />
            <span>
              {plural(station.open_holds, 'action', 'actions')} from this station{' '}
              {station.open_holds === 1 ? 'is' : 'are'} waiting for a manager.{' '}
              <Link to="/settings/held-actions" className="font-medium underline">
                Review held actions
              </Link>
            </span>
          </p>
        )}

        {stopped.length > 0 && (
          <p className="flex items-center gap-2 rounded-md bg-state-warning-subtle px-3 py-2 text-sm text-fg">
            <CirclePause aria-hidden className="size-4 shrink-0 text-state-warning" />
            <span>
              {stoppedSentence(stopped, data)} Its work waits on the device and is sent after the
              update.
            </span>
          </p>
        )}

        {active.length === 0 ? (
          <p className="text-body text-muted">
            No device serves this station yet. Pair a tablet or PC to start using it.
          </p>
        ) : (
          <Table>
            <TableCaption className="sr-only">Devices serving {station.name}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Device</TableHead>
                <TableHead>App version</TableHead>
                <TableHead>Last seen</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {active.map((d) => (
                <DeviceRow
                  key={d.id}
                  device={d}
                  station={station}
                  minVersion={data.min_app_versions[d.platform]}
                  onRevoked={revokedOne(d)}
                />
              ))}
            </TableBody>
          </Table>
        )}

        {revoked.length > 0 && (
          <details className="text-sm">
            <summary className="w-fit cursor-pointer rounded-sm text-muted">
              {plural(revoked.length, 'revoked device', 'revoked devices')}
            </summary>
            <ul className="mt-2 flex flex-col gap-1.5">
              {revoked.map((d) => (
                <li key={d.id} className="flex flex-wrap items-baseline gap-x-2 text-fg">
                  <span className="font-mono font-semibold">{d.device_code}</span>
                  <span className="text-muted">{PLATFORM_LABEL[d.platform]}</span>
                  <span className="text-muted">
                    revoked {d.revoked_at && <AccountDate value={d.revoked_at} />}
                    {d.revoke_reason ? `: ${d.revoke_reason}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </Card>
      <StationDialog
        branches={data.branches}
        station={station}
        open={editing}
        onOpenChange={setEditing}
      />
    </li>
  );
}

function MinVersionsCard({ data }: { data: StationsResponse }) {
  const [open, setOpen] = useState(false);
  const allBranches = useAllBranches();
  return (
    <section aria-labelledby="min-versions" className="mt-8">
      <Card className="gap-4 p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 id="min-versions" className="font-display text-lg font-semibold text-fg">
              Minimum app version
            </h2>
            <p className="text-body text-muted">
              A station on an older app stops syncing and asks to be updated; its work waits on it
              and is sent after the update.
            </p>
          </div>
          {allBranches && (
            <Button variant="secondary" icon={<Pencil aria-hidden />} onClick={() => setOpen(true)}>
              Change
            </Button>
          )}
        </div>
        <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-4">
          {PLATFORM_ORDER.map((p) => (
            <div key={p} className="flex flex-col">
              <dt className="text-sm text-muted">{PLATFORM_LABEL[p]}</dt>
              <dd className="text-body font-semibold tabular-nums text-fg">
                {data.min_app_versions[p]}
              </dd>
            </div>
          ))}
        </dl>
        {!allBranches && (
          <p className="text-sm text-muted">
            It applies to every branch, so only someone with all branches can change it.
          </p>
        )}
      </Card>
      <MinVersionsDialog
        current={data.min_app_versions}
        stations={data.stations}
        open={open}
        onOpenChange={setOpen}
      />
    </section>
  );
}

/** The page's loading shape: station cards stacked as they will be (UI3A-207). */
function StationsSkeleton() {
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-4">
      <span className="sr-only">Loading stations…</span>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex flex-col gap-4 rounded-lg border border-border bg-surface-raised p-5"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-56 max-w-full" />
            </div>
            <div className="hidden gap-2 sm:flex">
              <Skeleton className="h-control w-16" />
              <Skeleton className="h-control w-32" />
            </div>
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
    </div>
  );
}

function StationsPage() {
  useDocumentTitle('Stations & devices');
  const canManage = useCanManageDevices();
  const list = useQuery({ ...stationsQuery(), enabled: canManage === true });
  // Adding: the branch to start on (null = the dialog's own default).
  const [adding, setAdding] = useState<{ branchId?: string } | null>(null);
  // The station whose pairing dialog is open.
  const [pairingId, setPairingId] = useState<string | null>(null);
  // A station just added, outlined for a few seconds (UI3A-208).
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const clearHighlight = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(clearHighlight.current), []);
  const data = list.data;
  const several = (data?.branches.length ?? 0) > 1;
  const pairingStation = data?.stations.find((s) => s.id === pairingId);

  const added = (station: Station) => {
    setJustAdded(station.id);
    clearTimeout(clearHighlight.current);
    clearHighlight.current = setTimeout(() => setJustAdded(null), 8000);
    toast.success('Station added', {
      description: `${station.name}: pair a device to it next.`,
      action: { label: 'Pair a device', onClick: () => setPairingId(station.id) },
    });
  };

  return (
    <SettingsFrame>
      <SettingsBreadcrumb page="Stations & devices" />
      <PageHeader
        title="Stations & devices"
        description="A station is a job in the branch, like “Production 1” or “Receiving”. The tablet or PC serving it is paired once, with a one-time key."
        actions={
          data && data.stations.length > 0 ? (
            <Button icon={<Plus aria-hidden />} onClick={() => setAdding({})}>
              Add station
            </Button>
          ) : undefined
        }
      />
      {canManage === false ? (
        <NoDeviceAccess />
      ) : list.isError ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The stations"
          onRetry={() => void list.refetch()}
        />
      ) : !data ? (
        <StationsSkeleton />
      ) : data.stations.length === 0 ? (
        <EmptyState
          icon={TabletSmartphone}
          title="No stations yet"
          description="Add a station for each job in the branch, then pair the tablet or PC that does it."
          action={
            data.branches.length > 0 ? (
              <Button icon={<Plus aria-hidden />} onClick={() => setAdding({})}>
                Add station
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="flex flex-col gap-8">
          {data.branches.map((branch) => {
            const stations = data.stations.filter((s) => s.branch_id === branch.id);
            if (stations.length === 0 && !several) return null;
            return (
              <section key={branch.id} aria-label={several ? branch.name : undefined}>
                {several && (
                  <h2 className="mb-3 font-display text-xl font-semibold text-fg">{branch.name}</h2>
                )}
                {stations.length === 0 ? (
                  <Card className="flex-row flex-wrap items-center justify-between gap-3 p-5">
                    <p className="text-body text-muted">No stations in {branch.name} yet.</p>
                    <Button
                      variant="secondary"
                      icon={<Plus aria-hidden />}
                      aria-label={`Add a station to ${branch.name}`}
                      onClick={() => setAdding({ branchId: branch.id })}
                    >
                      Add station
                    </Button>
                  </Card>
                ) : (
                  <ul aria-label={`Stations in ${branch.name}`} className="flex flex-col gap-4">
                    {stations.map((s) => (
                      <StationCard
                        key={s.id}
                        station={s}
                        data={data}
                        headingLevel={several ? 'h3' : 'h2'}
                        highlighted={s.id === justAdded}
                        onPair={() => setPairingId(s.id)}
                      />
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
      {data && <MinVersionsCard data={data} />}
      {data && (
        <StationDialog
          branches={data.branches}
          branchId={adding?.branchId}
          open={adding !== null}
          onOpenChange={(o) => !o && setAdding(null)}
          onAdded={added}
        />
      )}
      {pairingStation && (
        <PairDeviceDialog
          station={pairingStation}
          open
          onOpenChange={(o) => !o && setPairingId(null)}
        />
      )}
    </SettingsFrame>
  );
}
